import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Building2, Users } from "lucide-react";

export default function ConferenceCard({ item, onClick }) {
  // Get all event types
  const eventTypes = item.event_type || [];

  // Format event type name
  const formatEventTypeName = (type) => {
    if (!type) return "Event";
    return type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Get color scheme for event type
  const getEventTypeColor = (type) => {
    const normalizedType = type?.toLowerCase() || "";
    
    if (normalizedType.includes("conference")) {
      return "text-white bg-blue-500/90";
    }
    if (normalizedType.includes("trade") || normalizedType.includes("mission")) {
      return "text-white bg-purple-500/90";
    }
    if (normalizedType.includes("workshop") || normalizedType.includes("seminar")) {
      return "text-white bg-orange-500/90";
    }
    if (normalizedType.includes("exhibition") || normalizedType.includes("expo")) {
      return "text-white bg-pink-500/90";
    }
    if (normalizedType.includes("festival") || normalizedType.includes("celebration")) {
      return "text-white bg-yellow-500/90";
    }
    if (normalizedType.includes("summit") || normalizedType.includes("forum")) {
      return "text-white bg-indigo-500/90";
    }
    return "text-white bg-emerald-500/90";
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const eventTitle = item.event_title || "Untitled Event";
  const thumbnail = item.thumbnail || "/images/placeholder.png";
  const startDate = item.event_start_date || item.event_date;
  const endDate = item.event_end_date || item.event_date;
  const eventDate =
    startDate && endDate && startDate !== endDate
      ? `${formatDate(startDate)} – ${formatDate(endDate)}`
      : formatDate(startDate || item.event_date);
  const startTime = item.event_start_time || item.event_time;
  const endTime = item.event_end_time || item.event_time;
  const eventTime =
    startTime && endTime && startTime !== endTime
      ? `${formatTime(startTime)} – ${formatTime(endTime)}`
      : startTime ? formatTime(startTime) : "";
  const venue = item.venue_name || "Venue TBA";
  const city = item.city || "";
  const country = item.country || "";
  const isActive = item.event_state === "active";
  const slots = item.overall_slots;
  
  // Get organizer name
  const getOrganizerName = () => {
    // Prefer explicit string fields
    if (typeof item.organizer_name === "string" && item.organizer_name.trim()) {
      return item.organizer_name.trim();
    }
    // organizer_name could be an object; try its name property
    if (
      item.organizer_name &&
      typeof item.organizer_name === "object" &&
      item.organizer_name.name
    ) {
      return item.organizer_name.name;
    }
    // organizer object with name
    if (item.organizer?.name) return item.organizer.name;
    // organizer passed as string
    if (typeof item.organizer === "string" && item.organizer.trim()) {
      return item.organizer.trim();
    }
    // Fallback to slug if present
    if (item.organizer_slug) return item.organizer_slug;
    return null;
  };
  const organizerName = getOrganizerName();

  return (
    <motion.div
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="overflow-hidden relative w-full h-[380px] sm:h-[420px] lg:h-[500px] text-left bg-white rounded-xl sm:rounded-2xl ring-1 ring-gray-200 shadow-sm transition-all duration-300 cursor-pointer group focus:outline-none hover:shadow-xl hover:ring-2 hover:ring-emerald-400 focus:ring-2 focus:ring-emerald-400"
    >
      <div className="flex flex-col h-full">
        {/* Image container */}
        <div className="overflow-hidden relative flex-1 min-h-0 bg-gradient-to-br from-gray-100 to-gray-200">
          <img
            src={thumbnail}
            alt={eventTitle}
            className="object-cover w-full h-full transition-all duration-500 group-hover:scale-110"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-300 from-black/60 via-black/20 group-hover:opacity-100" />
          
          {/* Event Type Badges */}
          <div className="absolute top-2 right-2 z-10 sm:top-3 sm:right-3">
            <div className="flex gap-1 justify-end items-center">
              {eventTypes.length > 0 ? (
                eventTypes.slice(0, 2).map((type, index) => (
                  <motion.span
                    key={`${type}-${index}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`inline-flex px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-semibold rounded-full backdrop-blur-sm whitespace-nowrap ${getEventTypeColor(type)}`}
                  >
                    {formatEventTypeName(type)}
                  </motion.span>
                ))
              ) : (
                <span className="inline-flex px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-semibold text-white rounded-full backdrop-blur-sm bg-gray-500/90">
                  Event
                </span>
              )}
              {eventTypes.length > 2 && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-semibold text-white rounded-full backdrop-blur-sm bg-gray-600/90"
                  title={eventTypes.slice(2).map(formatEventTypeName).join(", ")}
                >
                  +{eventTypes.length - 2}
                </motion.span>
              )}
            </div>
          </div>

          {/* Event State Badge */}
          {!isActive && (
            <div className="absolute bottom-2 left-2 z-10 sm:bottom-3 sm:left-3">
              <span className="inline-flex px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-semibold text-white rounded-full backdrop-blur-sm bg-red-500/90">
                Closed
              </span>
            </div>
          )}

          {/* Slots indicator */}
          {slots && (
            <div className="absolute right-2 bottom-2 z-10 sm:right-3 sm:bottom-3">
              <div className="flex gap-0.5 sm:gap-1 items-center px-1.5 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-medium text-white bg-black/50 rounded-full backdrop-blur-sm">
                <Users size={10} className="sm:w-3 sm:h-3" />
                <span>{slots} slots</span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col flex-shrink-0 p-3 sm:p-4 lg:p-5">
          {/* Title */}
          <h4 className="mb-2 text-xs font-bold leading-tight text-gray-900 transition-colors sm:text-sm lg:text-base line-clamp-2 group-hover:text-emerald-600">
            {eventTitle}
          </h4>

          {/* Event Details - Compact Layout */}
          <div className="space-y-1.5 sm:space-y-2">
            {/* Date and Time */}
            <div className="flex flex-wrap gap-2 sm:gap-3 items-center text-[10px] sm:text-xs text-gray-600">
              <div className="flex gap-1 sm:gap-1.5 items-center">
                <Calendar size={12} className="sm:w-3.5 sm:h-3.5 text-emerald-600 flex-shrink-0" />
                <span className="font-medium">{eventDate}</span>
              </div>
              {eventTime && (
                <div className="flex gap-1 sm:gap-1.5 items-center">
                  <Clock size={12} className="sm:w-3.5 sm:h-3.5 text-emerald-600 flex-shrink-0" />
                  <span>{eventTime}</span>
                </div>
              )}
            </div>

            {/* Venue */}
            <div className="flex gap-1 sm:gap-1.5 items-center text-[10px] sm:text-xs text-gray-600">
              <MapPin size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0 text-emerald-600" />
              <span className="line-clamp-1">
                {venue}
                {city && `, ${city}`}
                {country && `, ${country}`}
              </span>
            </div>
          </div>

          {/* Footer Info - Organizer Name */}
          {organizerName && (
            <div className="flex gap-1 sm:gap-1.5 items-center pt-2 sm:pt-3 text-[10px] sm:text-xs text-gray-600 border-t border-gray-100 mt-2 sm:mt-3">
              <Building2 size={12} className="sm:w-3.5 sm:h-3.5 text-emerald-600 flex-shrink-0" />
              <span className="font-medium line-clamp-1">{organizerName}</span>
            </div>
          )}
        </div>

        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-gradient-to-b transition-all duration-300 pointer-events-none from-emerald-50/0 via-emerald-50/0 to-emerald-50/0 group-hover:from-emerald-50/10 group-hover:via-emerald-50/5 group-hover:to-emerald-50/10" />
      </div>
    </motion.div>
  );
}

